/**
 * Permission choices for a new session, per run mode. A headless run cannot ask anyone, so
 * "Ask before acting" there means every tool not already allowed in the person's Claude
 * Code settings is refused. Headless therefore defaults to Accept edits; a terminal, where
 * someone can answer, keeps Ask before acting. Each mode remembers its own choice.
 */
import type { SessionStartMode } from '@orgus/protocol';
import { acpAgent, agentKindLabel } from '@orgus/protocol';
import { PERMISSION_MODES, permissionLabelFor } from '../sessions/labels.ts';
import type { Remembered } from './store.ts';

export type PermissionsByMode = Record<SessionStartMode, string>;

export const DEFAULT_PERMISSIONS: PermissionsByMode = { headless: 'acceptEdits', terminal: 'default' };

export function initialPermissions(r: Remembered): PermissionsByMode {
  const saved = r.permissionModes ?? {};
  // Older saves kept one choice for both modes; a headless "Ask" there was the old default, not a choice.
  const legacy = r.permissionMode;
  return {
    headless: saved.headless ?? (r.mode === 'headless' && legacy && legacy !== 'default' ? legacy : DEFAULT_PERMISSIONS.headless),
    terminal: saved.terminal ?? (r.mode === 'terminal' && legacy ? legacy : DEFAULT_PERMISSIONS.terminal),
  };
}

/** An ACP agent asks Orgus for every edit and command, so Orgus never starts one in a mode that skips that. */
const skipsApprovals = (kind: string, m: string) => !!acpAgent(kind) && m === 'bypassPermissions';

export function permissionOptions(mode: SessionStartMode, kind = 'claude-code'): { value: string; label: string; disabled?: boolean }[] {
  const acp = !!acpAgent(kind);
  return PERMISSION_MODES.map((m) => {
    if (skipsApprovals(kind, m)) return { value: m, label: `${permissionLabelFor(kind, m)} (not for ${agentKindLabel(kind)})`, disabled: true };
    const label =
      m === 'default' && mode === 'headless' && kind === 'claude-code'
        ? 'Ask (headless: tools not already allowed are refused)'
        : m === 'default' && acp
          ? 'Ask before acting (answered in Orgus)'
          : permissionLabelFor(kind, m);
    return { value: m, label };
  });
}

/** The permission choices with any the agent cannot take put back to Ask before acting. */
export function fitPermissions(p: PermissionsByMode, kind: string): PermissionsByMode {
  const fit = (m: string) => (skipsApprovals(kind, m) ? 'default' : m);
  const out = { headless: fit(p.headless), terminal: fit(p.terminal) };
  return out.headless === p.headless && out.terminal === p.terminal ? p : out;
}

export function modeHint(mode: SessionStartMode, permission: string, kind = 'claude-code'): string {
  const who = kind === 'claude-code' ? 'Claude' : agentKindLabel(kind);
  if (mode === 'terminal') return `Terminal opens a window on that machine, so someone there can answer ${who}.`;
  if (permission === 'default' && kind === 'claude-code') {
    return 'Headless cannot ask you. With Ask before acting, any tool not already allowed in your Claude Code settings is refused.';
  }
  if (acpAgent(kind)) return `Headless runs in the background and streams here. When ${who} asks for permission, it shows in Needs you.`;
  return 'Headless runs in the background and streams here. Its tool calls follow the permission setting.';
}
